import { useState } from 'react';
import {
  Grid, Button, InputLabel, TextField, Paper, Typography, Select,
  FormControl, MenuItem
} from '@mui/material';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete';
import axios from 'axios';
import PropTypes from 'prop-types';

import { isEmpty, puntoDeGoogle } from "../../common/helpers";

const MainDireccion = ({
  data, direcciones, errors, infoDire,
  guardar, cambiarData, setData, setErrors, eliminar
}) => {
  const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [idEditar, setIdEditar] = useState(null);
  const [toggleEdit, setToggleEdit] = useState(false);

  const guardarDireccion = async e => {
    e.preventDefault();
    const res = await guardar(idEditar);
    if (res) {
      setData({
        ...data,
        ...infoDire,
      });
    }
  }
  // Función para obtener detalles del lugar usando el place_id
  const fetchPlaceDetails = async (placeId) => {
    try {
      const results = await geocodeByPlaceId(placeId);
      
      if (!results || results.length === 0) {
        console.error('No se encontraron resultados para el place_id:', placeId);
        return;
      }

      const addressComponents = results[0].address_components;

      // Procesar los componentes de la dirección
      const details = { ...infoDire };

      addressComponents.forEach((component) => {
        const types = component.types;
        if (types.includes('street_number')) {
          details.numeroExt = component.long_name;
        }
        if (types.includes('route')) {
          details.calle = component.long_name;
        }
        if (types.includes('sublocality') || types.includes('neighborhood')) {
          details.colonia = component.long_name;
        }
        if (types.includes('locality')) {
          details.municipio = component.long_name;
        }
        if (types.includes('administrative_area_level_1')) {
          details.estado = component.long_name;
        }
        if (types.includes('country')) {
          switch (component.long_name) {
            case "United States":
              details.pais = "EEUU";
              break;
            case "Mexico":
              details.pais = "México";
              break;
            default:
              details.pais = component.long_name;
              break;
          }
        }
        if (types.includes('postal_code')) {
          details.cp = component.long_name;
        }
      });

      setData({
        ...data,
        numeroExt: details.numeroExt || '',
        estado: details.estado || '',
        calle: details.calle || '',
        colonia: details.colonia || '',
        municipio: details.municipio === "Ciudad de México" ? "" : (details.municipio || ''),
        cp: details.cp || '',
        pais: details.pais || '',
        referencia: details.referencia || '',
        place_id: placeId,
        direccion: results[0].formatted_address || `${details.calle} ${details.numeroExt}, ${details.colonia}, ${details.municipio}, ${details.estado}, ${details.cp}, ${details.pais}`,
        // Las coordenadas que ya dio Google: el servidor las guarda y el mapa de la página no tiene que volver a preguntar
        location: puntoDeGoogle(results[0].geometry?.location),
      });
    } catch (error) {
      console.error('Error al obtener detalles del lugar:', error);
    }
  };

  // Función para manejar la selección del lugar
  const handleSelect = (place) => {
    setSelectedPlace(place);
    // Obtener el place_id y fetch de detalles
    if (place && place.value.place_id) {
      fetchPlaceDetails(place.value.place_id);
    }
  };

  const editar = id => {
    const direccion = direcciones.find(d => d._id === id);
    setIdEditar(id);
    setToggleEdit(true);
    if (direccion) {
      setData({
        ...data,
        direccion: `${direccion.calle} ${direccion.numeroExt}, ${direccion.colonia}, ${direccion.municipio}, ${direccion.estado}, ${direccion.cp}, ${direccion.pais}`,
        calle: direccion.calle,
        numeroExt: direccion.numeroExt,
        numeroInt: direccion.numeroInt,
        cp: direccion.cp,
        colonia: direccion.colonia,
        municipio: direccion.municipio,
        nombre: direccion.nombre,
        estado: direccion.estado,
        referencia: direccion.referencia,
        place_id: direccion.place_id,
        pais: direccion.pais,
        // Sin las de otra dirección elegida antes: si el lugar no cambia, el servidor deja las que ya tenía
        location: null,
      });
    }
  }

  const limpiar = () => {
    setData({
      ...data,
      ...infoDire
    });
    setToggleEdit(false);
    setErrors({});
    setIdEditar(null);
    setSelectedPlace(null);
    setInputValue('');
  }

  return (
    <Grid container spacing={1} mb={2} mt={2} size={12}>
      {/* Header Section */}
      <Grid size={12} container justifyContent="space-between" alignItems="center" mb={3}>
        <Grid>
          <Typography variant="h3" color="primary">
            Direcciones
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gestiona tus direcciones de contacto
          </Typography>
        </Grid>
        <Grid>
          <Button
            variant="contained"
            color='primary'
            onClick={() => setToggleEdit(true)}
          >
            Nueva dirección
          </Button>
        </Grid>
      </Grid>

      <Paper sx={{ padding: 3, width: '100%' }}>
        {
          (isEmpty(direcciones) || toggleEdit) && (
            <Grid container spacing={2} size={12}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <InputLabel htmlFor="direccion" required>
                  Dirección
                </InputLabel>
                <GooglePlacesAutocomplete
                  apiKey={GOOGLE_API_KEY}
                  selectProps={{
                    value: selectedPlace,
                    onChange: handleSelect,
                    inputValue,
                    onInputChange: (value) => {
                      setInputValue(value);
                      if (value === '') {
                        setSelectedPlace(null); // Limpia el valor cuando el input está vacío
                      }
                    },
                    placeholder: 'Ingresa una dirección...',
                    styles: {
                      input: (provided) => ({
                        ...provided,
                        padding: '10px',
                        fontSize: '16px',
                      }),
                      option: (provided) => ({
                        ...provided,
                        fontSize: '14px',
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        fontSize: '16px',
                      }),
                    },
                  }}
                  autocompletionRequest={{
                    types: ['address'], // Restringe a direcciones (puedes cambiar a otros tipos como 'establishment')
                    componentRestrictions: { country: ['us', 'mx'] }, // Restringe a un país (opcional)
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <InputLabel htmlFor="select-pais" required>
                  País
                </InputLabel>
                <FormControl fullWidth>
                  <Select
                    id="pais"
                    displayEmpty
                    value={data.pais}
                    onChange={(e) => cambiarData("pais", e.target.value)}
                    size="small"
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return <em>Selecciona el país</em>;
                      }

                      return selected;
                    }}
                    {...errors.pais && { error: true, helperText: errors.pais }}
                  >
                    <MenuItem value="México">México</MenuItem>
                    <MenuItem value="EEUU">EEUU</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <InputLabel htmlFor="estado" required>
                  Estado
                </InputLabel>
                <TextField
                  {...errors.estado && { error: true, helperText: errors.estado }}
                  id="estado"
                  placeholder="Estado al que pertenece"
                  size="small"
                  variant="outlined"
                  fullWidth
                  autoComplete="off"
                  value={data.estado}
                  onChange={(e) => cambiarData("estado", e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <InputLabel htmlFor="municipio" required>
                  Municipio
                </InputLabel>
                <TextField
                  {...errors.municipio && { error: true, helperText: errors.municipio }}
                  id="municipio"
                  placeholder="Municipio al que pertenece"
                  size="small"
                  variant="outlined"
                  fullWidth
                  autoComplete="off"
                  value={data.municipio}
                  onChange={(e) => cambiarData("municipio", e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <InputLabel htmlFor="colonia" required>
                  Colonia
                </InputLabel>
                <TextField
                  {...errors.colonia && { error: true, helperText: errors.colonia }}
                  id="colonia"
                  placeholder="Colonia a la que pertenece"
                  size="small"
                  variant="outlined"
                  fullWidth
                  autoComplete="off"
                  value={data.colonia}
                  onChange={(e) => cambiarData("colonia", e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <InputLabel htmlFor="calle" required>
                  Calle
                </InputLabel>
                <TextField
                  {...errors.calle && { error: true, helperText: errors.calle }}
                  id="calle"
                  placeholder="Calle"
                  size="small"
                  variant="outlined"
                  fullWidth
                  autoComplete="off"
                  value={data.calle}
                  onChange={(e) => cambiarData("calle", e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <InputLabel htmlFor="cp" required>
                  C.P
                </InputLabel>
                <TextField
                  {...errors.cp && { error: true, helperText: errors.cp }}
                  id="cp"
                  placeholder="Tu código postal"
                  size="small"
                  variant="outlined"
                  fullWidth
                  autoComplete="off"
                  value={data.cp}
                  onChange={(e) => cambiarData("cp", e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <InputLabel htmlFor="numeroExt">
                  Número exterior
                </InputLabel>
                <TextField
                  id="numeroExt"
                  placeholder="Tu número exterior"
                  size="small"
                  variant="outlined"
                  fullWidth
                  autoComplete="off"
                  value={data.numeroExt}
                  onChange={(e) => cambiarData("numeroExt", e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <InputLabel htmlFor="numeroInt">
                  Número interior
                </InputLabel>
                <TextField
                  id="numeroInt"
                  placeholder="Tu número interior"
                  size="small"
                  variant="outlined"
                  fullWidth
                  autoComplete="off"
                  value={data.numeroInt}
                  onChange={(e) => cambiarData("numeroInt", e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <InputLabel htmlFor="nombre" required>
                  Nombre
                </InputLabel>
                <TextField
                  {...errors.nombre && { error: true, helperText: errors.nombre }}
                  id="nombre"
                  placeholder="¿A qué corresponde está dirección? Ejemplo: Casa, Oficina, Taller..."
                  size="small"
                  fullWidth
                  value={data.nombre}
                  onChange={(e) => cambiarData("nombre", e.target.value)}
                />
              </Grid>
              <Grid size={12}>
                <InputLabel htmlFor="referencia">
                  Referencia
                </InputLabel>
                <TextField
                  id="referencia"
                  placeholder="Referencia de la dirección"
                  size="small"
                  fullWidth
                  value={data.referencia}
                  onChange={(e) => cambiarData("referencia", e.target.value)}
                  rows={3}
                  multiline
                />
              </Grid>
              <Grid container size={12} sx={{ justifyContent: "flex-end" }}>
                <Button
                  type="button"
                  variant="outlined"
                  size="large"
                  onClick={limpiar}
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  size="large"
                  onClick={e => guardarDireccion(e)}
                >
                  Guardar
                </Button>
              </Grid>
            </Grid>
          )
        }

        <Grid size={12} mt={3}>
          <Grid container spacing={2} sx={{ maxHeight: '50vh', overflowY: "auto" }}>
            {
              !isEmpty(direcciones) ? (
                direcciones.map(info => (
                  <Grid size={{ xs: 12, md: 6}} key={info._id}>
                    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                      <Grid container spacing={2}>
                        <Grid size={12}>
                          <Typography variant="h6" color="primary" fontWeight="bold">
                            {info.nombre}
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 1 }}>
                            {info.direccion}
                          </Typography>
                        </Grid>

                        <Grid size={12} container alignItems="center" justifyContent="space-between" mt={2}>
                          <Grid>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {info.municipio || info.estado}
                            </Typography>
                          </Grid>
                          <Grid container>
                            <Button
                              variant="contained"
                              onClick={() => editar(info._id)}
                              color='secondary'
                              endIcon={<DriveFileRenameOutlineIcon />}
                            >
                              Editar
                            </Button>
                            <Button
                              variant="contained"
                              onClick={() => eliminar(info._id)}
                              color='error'
                              endIcon={<DeleteOutlineIcon />}
                            >
                              Eliminar
                            </Button>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                ))
              ) : (
                <Grid size={12}>
                  <Typography variant='body1' color="text.secondary" align="center">
                    No tienes direcciones registradas aún.
                  </Typography>
                </Grid>
              )
            }
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
};

MainDireccion.propTypes = {
  direcciones: PropTypes.array,
  infoDire: PropTypes.object,
  data: PropTypes.object.isRequired,
  errors: PropTypes.object,
  setData: PropTypes.func.isRequired,
  cambiarData: PropTypes.func.isRequired,
  guardar: PropTypes.func.isRequired,
  eliminar: PropTypes.func.isRequired,
};

export default MainDireccion;